import { useEffect } from 'react'

import { useEditDoor } from '../hooks/useEditDoor'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ColorScheme, Stack } from '@hivewatch/dark'

import { DarkEditDeviceSkeleton } from '../../dark/DarkEditDeviceSkeleton'
import { FormDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'
import { DarkDeviceDetailsSection } from './DarkDeviceDetailsSection'
import { DarkThresholdConfigurationSection } from './DarkThresholdConfigurationSection'

interface DarkEditDoorFormIProps {
  colorScheme: ColorScheme
  deviceId: string
  onLoadingChange: (loading: boolean) => void
  onSave?: () => void
}

export const DarkEditDoorForm = ({
  colorScheme,
  deviceId,
  onLoadingChange,
  onSave,
}: DarkEditDoorFormIProps) => {
  const { t } = useTranslation()
  const form = useForm<FormDataTypeI>({ mode: 'onChange' })
  const { handleSubmit, errors, control, formState, setValue, trigger } = form
  const { shouldEnableUpdateDevice, isInternalImplementer } = usePermissions()
  const isTraceLogsEnabled = useFeatureFlag(FeatureFlag.traceLogs)
  const shouldShowThresholdPanel = isTraceLogsEnabled && isInternalImplementer
  const { isDirty, isValid } = formState
  const {
    deviceData,
    isLoading,
    floorsOptions,
    isUpdateDeviceLoading,
    isUpdateDoorLoading,
    updateDoor,
  } = useEditDoor(deviceId)
  const { showSuccess, showError } = useToasts()

  const onSubmit: SubmitHandler<FormDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location.value,
      manufacturerName: values.manufacturerName,
    }

    try {
      await updateDoor(formData, values.externalId)
      showSuccess(t('devices:crud_device_update_success'))
      onSave?.()
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'update',
          feature: 'edit_door',
          surface: 'dark_edit_device_modal',
        },
        extra: { deviceId },
      })
      showError()
    }
  }

  useEffect(() => {
    if (isDirty || isLoading) return

    setValue('name', deviceData?.device?.name, { shouldValidate: true })
    setValue(
      'location',
      floorsOptions.find(
        (option) => option.value === deviceData?.device?.floor?.id
      ),
      { shouldValidate: true }
    )
    setValue('externalId', deviceData?.device?.door?.externalId)
    setValue('manufacturerName', deviceData?.device?.manufacturerName)
    trigger(['name'])
  }, [deviceData, floorsOptions, isDirty, isLoading, setValue, trigger])

  useEffect(() => {
    onLoadingChange(isLoading)
  }, [isLoading, onLoadingChange])

  if (!deviceData || isLoading) return <DarkEditDeviceSkeleton />

  return (
    <Stack gap='16px'>
      <DarkDeviceDetailsSection
        colorScheme={colorScheme}
        control={control}
        errors={errors}
        floorsOptions={floorsOptions}
        isDevicePlaced={isPlaced(deviceData.device)}
        isSaveDisabled={!isValid || !shouldEnableUpdateDevice}
        isSaveLoading={isUpdateDeviceLoading || isUpdateDoorLoading}
        onSave={handleSubmit(onSubmit)}
      />

      {shouldShowThresholdPanel && (
        <DarkThresholdConfigurationSection
          colorScheme={colorScheme}
          deviceId={deviceId}
          onSaveCallback={onSave}
        />
      )}
    </Stack>
  )
}
